import { forwardRef, useImperativeHandle, useState, useContext } from "react";
import {
  MdAdd,
  MdClose,
  MdCorporateFare,
  MdOutlineExpandMore,
  MdOutlineSave,
  MdOutlineSupervisedUserCircle,
  MdOutlineWorkspacePremium,
  MdPersonAddAlt,
  MdSystemUpdateAlt,
} from "react-icons/md";
import { AnimatePresence, motion } from "framer-motion";
import { DropdownItemProps } from "../components/dropdown-menu/DropdownItem";
import DropdownMenuPopUp from "../pop-up-panels/dropdown-menu-pop-up";
import { MainContext } from "components/utils/Contexts";
import { singlify } from "utils/utils";
import { usePlatform } from "components/utils/PlatformContext";

interface DashboardHeaderOptionsProps {
  ref: any;
  dashboardRef: React.MutableRefObject<any>;
}

const DashboardHeaderOptions: React.FC<DashboardHeaderOptionsProps> =
  forwardRef(({ dashboardRef }, ref) => {
    // States definition
    const [showCreateNewPopUp, setShowCreateNewPopUp] = useState(false);
    const [showGenerateReportsPopUp, setShowGenerateReportsPopUp] =
      useState(false);
    const [showCreateNew, setShowCreateNew] = useState(true);
    const [showGenerateReportsButton, setShowGenerateReportsButton] =
      useState(false);
    const [saveButton, setSaveButton] = useState<{
      show: boolean;
      disabled: boolean;
    }>({ show: false, disabled: true });
    // State for controlling what create pop up I should display on master admin dashboard
    const [createTypeButton, setCreateTypeButton] = useState<
      "customers" | "classes"
    >("customers");

    // Get the values from the context
    const { classesTerminology, studentsTerminology, schoolTerminology } =
      usePlatform();
    const { userRole } = useContext(MainContext);

    /**
     * Define the list of functions that can be used from external components
     * using a reference
     */
    useImperativeHandle(ref, () => ({
      setShowCreateNewButton(bool: boolean) {
        setShowCreateNew(bool);
      },
      setShowGenerateReportsButton(bool: boolean) {
        setShowGenerateReportsButton(bool);
      },
      setShowCreateNewPopUp(bool: boolean) {
        setShowCreateNewPopUp(bool);
      },
      setSaveButton(options: { show: boolean; disabled: boolean }) {
        setSaveButton(options);
      },
      setCreateTypeButton(type: "customers" | "classes") {
        setCreateTypeButton(type);
      },
      setShowGenerateReportsPopUp(bool: boolean) {
        setShowGenerateReportsPopUp(bool);
      },
    }));

    /**
     * Defining the options for the create button dropdown list
     */
    const createNewSchoolOptions: DropdownItemProps[] = [
      {
        text: singlify(schoolTerminology),
        icon: <MdCorporateFare className="w-full h-full" />,
        onClickFunction: () => onCreateEditSchool(),
      },
    ];
    const createNewClassOptions: DropdownItemProps[] = [
      {
        text: singlify(classesTerminology),
        icon: <MdOutlineSupervisedUserCircle className="w-full h-full" />,
        onClickFunction: () => onCreateClass(),
      },
      {
        text: singlify(studentsTerminology),
        icon: <MdPersonAddAlt className="w-full h-full" />,
        onClickFunction: () => onCreateEditStudent(),
      },
    ];
    const createNewBrandOptions: DropdownItemProps[] = [
      {
        text: "Brand",
        icon: <MdOutlineWorkspacePremium className="w-full h-full" />,
        onClickFunction: () => onCreateEditBrand(),
      },
    ];
    const generateReportOptions: DropdownItemProps[] = [
      {
        text: "Export as PDF",
        icon: <MdOutlineSupervisedUserCircle className="w-full h-full" />,
        onClickFunction: () => generateReport("pdf"),
      },
      {
        text: "Export as CSV",
        icon: <MdPersonAddAlt className="w-full h-full" />,
        onClickFunction: () => generateReport("csv"),
      },
    ];

    /**
     * Function that executes the create lessons
     * defined on dashboard/index.tsx
     */
    // Create class
    const onCreateClass = () => {
      // Execute the function
      dashboardRef.current.onCreateClass(true);
    };

    /**
     * Function that executes the create edit student
     * defined on dashboard/index.tsx
     */
    const onCreateEditStudent = (
      title = `New ${singlify(studentsTerminology)}`,
      description = singlify(studentsTerminology)
    ) => {
      // Execute the function
      dashboardRef.current.onCreateEditStudent(title, description);
    };

    /**
     * Function that executes the create edit school
     * defined on dashboard/index.tsx
     */
    const onCreateEditSchool = (
      title = `New ${singlify(schoolTerminology)}`,
      description = singlify(schoolTerminology)
    ) => {
      // Execute the function
      dashboardRef.current.onCreateEditSchool(title, description);
    };

    /**
     * Function that executes the create edit school
     * defined on dashboard/index.tsx
     */
    const onCreateEditBrand = (title = "New Brand", description = "Brand") => {
      // Execute the function
      dashboardRef.current.onCreateEditBrand(title, description);
    };

    /**
     * Function that executes the discard function
     * defined on dashboard/index.tsx
     */
    const onDiscard = () => {
      // Execute the function
      dashboardRef.current.onDiscard();
    };

    /**
     * Function that executes the generate report function
     * by getting the defined classId on the
     * dashboard/index.tsx file
     */
    const generateReport = async (format: "csv" | "pdf") => {
      // Execute the function
      dashboardRef.current.generateReport(format);
    };

    /**
     * Function to control the save button
     */
    const handleSave = () => {
      // If the save button is available, then make it work
      if (!saveButton.disabled) dashboardRef.current.pressSaveButton();
    };

    return (
      <>
        {["master admin", "customer admin", "class admin"].includes(userRole) &&
          showGenerateReportsButton && (
            <motion.div
              className="h-24 p-3 rounded-full flex-col justify-center items-center gap-2 inline-flex select-none relative"
              initial={{
                opacity: 0,
              }}
              animate={{
                opacity: 1,
              }}
              exit={{
                opacity: 0,
              }}
              onClick={() => {
                if (!showGenerateReportsPopUp)
                  setShowGenerateReportsPopUp(true);
              }}
            >
              <div className="justify-center items-center gap-0.5 inline-flex cursor-pointer">
                <div className="w-4 h-4" />
                <div className="p-3 bg-Canvas rounded-full backdrop-blur-[32px] justify-center items-center gap-2 flex hover:bg-neutral-100 ease-in-out duration-300">
                  <div className="w-6 h-6 relative">
                    <MdSystemUpdateAlt className="w-full h-full text-secondary-600" />
                  </div>
                </div>
                <div className="w-4 h-4 relative">
                  <MdOutlineExpandMore className="w-4 h-4 text-secondary-600" />
                </div>
              </div>
              <div className="justify-center items-center gap-0.5 inline-flex">
                <div className="text-center text-secondary-600 text-xs font-semibold font-sans uppercase leading-[14px] tracking-wide">
                  Generate Report
                </div>
              </div>
              <AnimatePresence>
                {showGenerateReportsPopUp && (
                  <DropdownMenuPopUp
                    containerStyle="absolute w-[190px] top-[90px] right-2 p-3 bg-white rounded-lg shadow flex-col justify-center items-center gap-2 inline-flex cursor-pointer z-30"
                    optionsList={generateReportOptions}
                    setShowPopUp={setShowGenerateReportsPopUp}
                  />
                )}
              </AnimatePresence>
            </motion.div>
          )}
        {userRole === "root admin" && showCreateNew && (
          <motion.div
            className="p-3 rounded-full flex-col justify-center items-center gap-2 inline-flex relative"
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 1,
            }}
            exit={{
              opacity: 0,
            }}
          >
            <div
              className="justify-center items-center gap-0.5 inline-flex cursor-pointer"
              onClick={() => {
                if (!showCreateNewPopUp)
                  setShowCreateNewPopUp(!showCreateNewPopUp);
              }}
            >
              <div className="p-3 bg-neutral-50 rounded-full backdrop-blur-[32px] justify-center items-center gap-2 flex hover:bg-neutral-100 ease-in-out duration-150">
                <div className="w-6 h-6 relative">
                  <MdAdd className="w-full h-full fill-secondary-600" />
                </div>
              </div>
              <div className="w-4 h-4 relative">
                <MdOutlineExpandMore className="w-4 h-4 text-secondary-600" />
              </div>
            </div>
            <div className="justify-center items-center gap-0.5 inline-flex">
              <div className="text-center text-secondary-600 text-xs font-semibold font-sans uppercase leading-[14px] tracking-wide whitespace-nowrap select-none">
                Create
              </div>
              <div className="w-4 h-4 px-[4.28px] opacity-0 justify-center items-center flex" />
            </div>
            <AnimatePresence>
              {showCreateNewPopUp && (
                <DropdownMenuPopUp
                  containerStyle="absolute w-[154px] top-[90px] right-2 p-3 bg-white rounded-lg shadow flex-col justify-center items-center gap-2 inline-flex z-30"
                  optionsList={createNewBrandOptions}
                  setShowPopUp={setShowCreateNewPopUp}
                />
              )}
            </AnimatePresence>
          </motion.div>
        )}
        {userRole === "master admin" && showCreateNew && (
          <motion.div
            className="p-3 rounded-full flex-col justify-center items-center gap-2 inline-flex relative"
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 1,
            }}
            exit={{
              opacity: 0,
            }}
          >
            <div
              className="justify-center items-center gap-0.5 inline-flex cursor-pointer"
              onClick={() => {
                if (!showCreateNewPopUp)
                  setShowCreateNewPopUp(!showCreateNewPopUp);
              }}
            >
              <div className="p-3 bg-neutral-50 rounded-full backdrop-blur-[32px] justify-center items-center gap-2 flex hover:bg-neutral-100 ease-in-out duration-150">
                <div className="w-6 h-6 relative">
                  <MdAdd className="w-full h-full fill-secondary-600" />
                </div>
              </div>
              <div className="w-4 h-4 relative">
                <MdOutlineExpandMore className="w-4 h-4 text-secondary-600" />
              </div>
            </div>
            <div className="justify-center items-center gap-0.5 inline-flex">
              <div className="text-center text-secondary-600 text-xs font-semibold font-sans uppercase leading-[14px] tracking-wide whitespace-nowrap select-none">
                Create
              </div>
              <div className="w-4 h-4 px-[4.28px] opacity-0 justify-center items-center flex" />
            </div>
            <AnimatePresence>
              {showCreateNewPopUp && (
                <>
                  {createTypeButton === "customers" ? (
                    <DropdownMenuPopUp
                      containerStyle="absolute w-[179px] top-[90px] right-2 p-3 bg-white rounded-lg shadow flex-col justify-center items-center gap-2 inline-flex z-30"
                      optionsList={createNewSchoolOptions}
                      setShowPopUp={setShowCreateNewPopUp}
                    />
                  ) : createTypeButton === "classes" ? (
                    <DropdownMenuPopUp
                      containerStyle="absolute w-[154px] top-[90px] right-2 p-3 bg-white rounded-lg shadow flex-col justify-center items-center gap-2 inline-flex z-30"
                      optionsList={createNewClassOptions}
                      setShowPopUp={setShowCreateNewPopUp}
                    />
                  ) : (
                    <></>
                  )}
                </>
              )}
            </AnimatePresence>
          </motion.div>
        )}
        {["customer admin", "class admin"].includes(userRole) &&
          showCreateNew && (
            <motion.div
              className="p-3 rounded-full flex-col justify-center items-center gap-2 inline-flex relative"
              initial={{
                opacity: 0,
              }}
              animate={{
                opacity: 1,
              }}
              exit={{
                opacity: 0,
              }}
            >
              <div
                className="justify-center items-center gap-0.5 inline-flex cursor-pointer"
                onClick={() => {
                  if (!showCreateNewPopUp)
                    setShowCreateNewPopUp(!showCreateNewPopUp);
                }}
              >
                <div className="p-3 bg-neutral-50 rounded-full backdrop-blur-[32px] justify-center items-center gap-2 flex hover:bg-neutral-100 ease-in-out duration-150">
                  <div className="w-6 h-6 relative">
                    <MdAdd className="w-full h-full fill-secondary-600" />
                  </div>
                </div>
                <div className="w-4 h-4 relative">
                  <MdOutlineExpandMore className="w-4 h-4 text-secondary-600" />
                </div>
              </div>
              <div className="justify-center items-center gap-0.5 inline-flex">
                <div className="text-center text-secondary-600 text-xs font-semibold font-sans uppercase leading-[14px] tracking-wide whitespace-nowrap select-none">
                  Create
                </div>
                <div className="w-4 h-4 px-[4.28px] opacity-0 justify-center items-center flex" />
              </div>
              <AnimatePresence>
                {showCreateNewPopUp && (
                  <DropdownMenuPopUp
                    containerStyle="absolute w-[154px] top-[90px] right-2 p-3 bg-white rounded-lg shadow flex-col justify-center items-center gap-2 inline-flex z-30"
                    optionsList={createNewClassOptions}
                    setShowPopUp={setShowCreateNewPopUp}
                  />
                )}
              </AnimatePresence>
            </motion.div>
          )}
        {saveButton.show && (
          <div className="justify-start items-start gap-1 flex">
            <div className="p-3 rounded-full flex-col justify-center items-center gap-2 inline-flex">
              <div className="justify-center items-center gap-0.5 inline-flex">
                <div
                  className="p-3 bg-neutral-50 rounded-full backdrop-blur-[32px] justify-center items-center gap-2 flex cursor-pointer hover:bg-neutral-100 ease-in-out duration-150"
                  onClick={onDiscard}
                >
                  <div className="w-6 h-6 relative">
                    <MdClose className="w-full h-full fill-secondary-600" />
                  </div>
                </div>
              </div>
              <div className="justify-center items-center gap-0.5 inline-flex">
                <div className="text-center text-secondary-600 text-xs font-semibold font-sans uppercase leading-[14px] tracking-wide">
                  Discard
                </div>
              </div>
            </div>
            <div className="p-3 rounded-full flex-col justify-center items-center gap-2 inline-flex">
              <div
                className={`justify-center items-center gap-0.5 inline-flex ${
                  saveButton.disabled ? "cursor-not-allowed" : "cursor-pointer"
                }`}
                onClick={handleSave}
              >
                <div
                  className={`p-3 bg-neutral-50 rounded-full backdrop-blur-[32px] justify-center items-center gap-2 flex${
                    !saveButton.disabled &&
                    " hover:bg-neutral-100 ease-in-out duration-150"
                  }`}
                >
                  <div className="w-6 h-6 relative">
                    <MdOutlineSave
                      className={`w-full h-full ${
                        saveButton.disabled
                          ? "fill-neutral-300"
                          : "fill-secondary-600"
                      }
                      `}
                    />
                  </div>
                </div>
              </div>
              <div className="justify-center items-center gap-0.5 inline-flex">
                <div
                  className={`text-center text-xs font-semibold font-sans uppercase leading-[14px] tracking-wide whitespace-nowrap ${
                    saveButton.disabled
                      ? "text-neutral-300"
                      : "text-secondary-600"
                  }`}
                >
                  Save
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  });

export default DashboardHeaderOptions;
